import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { Loader2 } from 'lucide-react'

import {
  ProtoDropdown,
  ProtoInput,
  ProtoNumberInput,
  ProtoTextarea,
} from '@/features/studio/components/ui-kit'
import { Button } from '@/shared/components/ui/button'

export type ShotEditableField =
  | 'shot_size'
  | 'camera_angle'
  | 'camera_movement'
  | 'description'
  | 'dialogue'
  | 'sound_note'
  | 'music_note'
  | 'storyboard_note'
  | 'duration_sec'

type InlineCellProps = {
  field: ShotEditableField
  display: string
  rawValue: string | number
  canEdit: boolean
  isEditing: boolean
  saving: boolean
  center?: boolean
  multiline?: boolean
  options?: readonly string[]
  placeholder?: string
  onStartEdit: () => void
  onCancel: () => void
  onSave: (value: string | number) => void
}

export function StoryboardInlineCell({
  display,
  rawValue,
  canEdit,
  isEditing,
  saving,
  center,
  multiline,
  options,
  placeholder,
  onStartEdit,
  onCancel,
  onSave,
}: InlineCellProps) {
  const [draft, setDraft] = useState(String(rawValue ?? ''))

  useEffect(() => {
    if (isEditing) setDraft(String(rawValue ?? ''))
  }, [isEditing, rawValue])

  function commit() {
    if (options) {
      onSave(draft)
      return
    }
    if (typeof rawValue === 'number') {
      const n = Number(draft)
      onSave(Number.isFinite(n) && n > 0 ? n : rawValue)
      return
    }
    onSave(draft)
  }

  if (isEditing) {
    return (
      <td className={clsx('cell-editing', center && 'text-center')}>
        {saving ? (
          <span className="inline-flex items-center justify-center gap-1 text-zinc-500">
            <Loader2 className="h-3 w-3 animate-spin" />
          </span>
        ) : options ? (
          <ProtoDropdown
            value={draft}
            onValueChange={(v) => {
              setDraft(v)
              onSave(v)
            }}
            compact
            options={[{ value: '', label: '—' }, ...options.map((o) => ({ value: o, label: o }))]}
            className="cell-inline-dropdown"
          />
        ) : multiline ? (
          <ProtoTextarea
            autoFocus
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => commit()}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault()
                onCancel()
              }
            }}
            placeholder={placeholder}
            className="cell-inline-input"
          />
        ) : typeof rawValue === 'number' ? (
          <ProtoNumberInput
            autoFocus
            min={1}
            max={600}
            value={Number(draft) || 1}
            onValueChange={(v) => setDraft(String(v))}
            onBlur={() => commit()}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault()
                onCancel()
              }
              if (e.key === 'Enter') {
                e.preventDefault()
                commit()
              }
            }}
            className="cell-inline-input !text-center"
          />
        ) : (
          <ProtoInput
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => commit()}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault()
                onCancel()
              }
              if (e.key === 'Enter') {
                e.preventDefault()
                commit()
              }
            }}
            placeholder={placeholder}
            className="cell-inline-input"
          />
        )}
      </td>
    )
  }

  return (
    <td
      className={clsx(
        'cell-display',
        center && 'text-center',
        canEdit && 'cell-editable',
        display.trim() ? 'text-zinc-300' : 'text-zinc-500',
      )}
      onClick={() => {
        if (canEdit) onStartEdit()
      }}
      onKeyDown={(e) => {
        if (canEdit && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          onStartEdit()
        }
      }}
      role={canEdit ? 'button' : undefined}
      tabIndex={canEdit ? 0 : undefined}
    >
      {display.trim() ? (
        <span className={center ? 'block text-center' : undefined}>{display}</span>
      ) : (
        <span className="cell-empty">—</span>
      )}
    </td>
  )
}

export function StoryboardFrameCell({
  preview,
  canEdit,
  onPreview,
  onPick,
}: {
  preview?: string
  canEdit: boolean
  onPreview: () => void
  onPick: () => void
}) {
  const { t } = useTranslation()

  if (preview) {
    return (
      <td className="cell-frame">
        <div className="relative inline-block">
          <Button
            type="button"
            variant="ghost"
            onClick={onPreview}
            className="cell-frame-btn h-auto cursor-zoom-in p-0 hover:border-indigo-500/50"
          >
            <img src={preview} alt="" />
          </Button>
          {canEdit ? (
            <Button
              type="button"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation()
                onPick()
              }}
              className="cell-frame-replace h-auto p-0 font-normal"
              title={t('shots.replaceFrame')}
            >
              {t('shots.replaceFrame')}
            </Button>
          ) : null}
        </div>
      </td>
    )
  }

  if (!canEdit) {
    return (
      <td className="cell-frame">
        <span className="cell-empty">—</span>
      </td>
    )
  }

  return (
    <td className="cell-frame">
      <Button type="button" variant="ghost" onClick={onPick} className="cell-frame-add h-auto font-normal">
        <span className="cell-frame-add-icon">+</span>
        {t('shots.addFrame')}
      </Button>
    </td>
  )
}
